/* eslint-disable cyclomatic-complexity/zee-codeBlockComplexity */
//This file handles contents with multi-season.
import { useEffect, useState } from 'react';

import { GetServerSidePropsContext, NextPage } from 'next';
import { useRouter } from 'next/router';

import EpisodesPageContainer from 'src/components/episodesPageContainer/EpisodesPageContainer';
import Fallback from 'src/components/Fallback/Fallback';
import {
  getDetailsDataOnServer,
  getLicenseDataOnServer
} from 'src/services/apiRequests/serverSideProps/EpisodePageService';
import {
  browseByTypeAPICall,
  GenreLangFilterType
} from 'src/services/apiRequests/TvShowDetailsPage/GenreLanguageService';
import { getTvShowDetails } from 'src/services/apiRequests/TvShowDetailsPage/TvShowDetailsPageService';
import { TvShowRelatedContent } from 'src/services/GraphQL/Queries/types';
import SimpleStore from 'src/store/SimpleStore';
import { DynamicObject } from 'src/utils/commonTypes';
import {
  HTTP_HEADER_KEYS,
  HTTP_STATUS_CODE
} from 'src/utils/constants/HttpMethodsConstants';
import { TvShowDetailsHandleEnums } from 'src/utils/constants/stringConstants';
import {
  AssetsSubType,
  BreadCrumbItem,
  BrowseItemsType
} from 'src/utils/constants/TvShowDetailsPageConstants';
import {
  generateMultipleSeasonRedirectionUrl,
  getActiveSeasonIndex,
  getCurrentSeasonIndexFromUrl
} from 'src/utils/TvShowDetailsHelper/episodePageHelper';
import {
  episodePageSeoPropsGenerator,
  generateBreadCrumbsForEpisodePage
} from 'src/utils/TvShowDetailsHelper/tvShowDetailsSeoGenerator';

import {
  APP_CONFIG,
  DEVICE_WIDTH_CONSTANTS,
  EPISODE_TRANSLATION,
  INITIAL_LAUNCH
} from '../../../../../../utils/constants/utilConstants';

type Props = {
  tvShowData: TvShowRelatedContent;
  activeSeasonFromServerSide: number;
  seoData: DynamicObject;
  subHeaderItems: [BrowseItemsType];
  breadCrumbItems: BreadCrumbItem[];
};
const Episodes: NextPage<Props> = ({
  tvShowData,
  activeSeasonFromServerSide,
  seoData,
  subHeaderItems,
  breadCrumbItems
}) => {
  const [detailsData, setTvShowDetails] =
    useState<TvShowRelatedContent>(tvShowData);
  const [pageLoading, setPageLoading] = useState<boolean>(true);
  const [activeSeasonIndex, setActiveSeason] = useState<number>(
    activeSeasonFromServerSide
  );
  const router = useRouter();

  useEffect(() => {
    if (!detailsData) {
      getTvShowData();
    }
  }, [JSON.stringify(detailsData)]);

  useEffect(() => {
    const seasonIndexFromRouter = router?.query?.episodeName_seasonNo as string;
    setActiveSeason(getCurrentSeasonIndexFromUrl(seasonIndexFromRouter));
  }, []);

  const getTvShowData = async () => {
    const { showId } = router.query;
    const header = [HTTP_HEADER_KEYS.X_ACCESS_TOKEN];
    if (SimpleStore.get(HTTP_HEADER_KEYS.X_ACCESS_TOKEN)) {
      const data = await getTvShowDetails(showId as string, header);
      if (data && Object.keys(data).length) {
        setTvShowDetails(data);
      } else {
        setPageLoading(false);
      }
    }
  };

  return (
    <>
      {pageLoading ? (
        <EpisodesPageContainer
          breadCrumbItems={breadCrumbItems}
          detailsData={detailsData}
          activeSeasonIndexFromProps={activeSeasonIndex}
          seoData={seoData}
          subHeaderItems={subHeaderItems}
        />
      ) : (
        <Fallback type={TvShowDetailsHandleEnums?.SOME_THING_WRONG} />
      )}
    </>
  );
};
// for server side api calling
export async function getServerSideProps(context: GetServerSidePropsContext) {
  const appConfig = SimpleStore.get(APP_CONFIG);
  const initialLaunchData = SimpleStore.get(INITIAL_LAUNCH);
  const episodeTranslation = SimpleStore.get(EPISODE_TRANSLATION);
  const id = context?.params?.showId as string;
  const seasonIndexFromUrl = (context?.params?.episodeName_seasonNo ||
    1) as string;
  const contextUrl = decodeURI(context?.req?.url as string);
  const urlParams =
    (contextUrl.length &&
      contextUrl.includes('?') &&
      contextUrl.split('?')[1]) ||
    '';
  let data: TvShowRelatedContent | null = null;
  let breadCrumbItems: BreadCrumbItem[] = [];
  let seoData = {};
  let subHeaderItems: BrowseItemsType[] = [];
  data = await getDetailsDataOnServer(id);

  const patternToExcludePathname = /_next|.((js|json|map|ico|svg)$)/;
  const isRestrictedPathname = patternToExcludePathname.test(contextUrl);
  if (!isRestrictedPathname) {
    const seasonIndexFromRouter = context.query?.episodeName_seasonNo as string;
    let redirectUrl =
      data && generateMultipleSeasonRedirectionUrl(seasonIndexFromRouter, data);

    if (redirectUrl) {
      redirectUrl = urlParams ? `${redirectUrl}?${urlParams}` : redirectUrl;
      if (contextUrl !== redirectUrl) {
        return {
          redirect: {
            destination: encodeURI(redirectUrl),
            statusCode: HTTP_STATUS_CODE.MOVED_PERMANENTLY
          }
        };
      }
    }
  }

  if (data && Object.keys(data).length) {
    const [licenseData, subHeaderItemsResponse] = await Promise.all([
      await getLicenseDataOnServer(id),
      (appConfig?.deviceWidth || 0) < DEVICE_WIDTH_CONSTANTS.deviceWidth1200
        ? await browseByTypeAPICall(
            data?.assetSubType || AssetsSubType.TVSHOW,
            GenreLangFilterType.genre,
            true
          )
        : []
    ]);
    subHeaderItems = subHeaderItemsResponse || [];

    breadCrumbItems = generateBreadCrumbsForEpisodePage(
      context,
      episodeTranslation?.episodes,
      data as TvShowRelatedContent
    );

    seoData = await episodePageSeoPropsGenerator({
      detailsData: data as TvShowRelatedContent,
      allDisplayLanguages: initialLaunchData?.getlang?.display || [],
      activeSeasonIndex: getActiveSeasonIndex(
        getCurrentSeasonIndexFromUrl(seasonIndexFromUrl),
        data as TvShowRelatedContent
      ),
      breadCrumbItems,
      licenseData
    });
  }
  return {
    props: {
      tvShowData: data,
      activeSeasonFromServerSide: getActiveSeasonIndex(
        getCurrentSeasonIndexFromUrl(seasonIndexFromUrl),
        data as TvShowRelatedContent
      ),
      seoData,
      subHeaderItems,
      breadCrumbItems
    }
  };
}

export default Episodes;
